import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { FileText, CheckCircle2, AlertCircle, Calendar, User, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ErsatzbescheinigungPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    courseDate: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Auto-Scroll nach oben wenn Erfolgsansicht gerendert wird
  useEffect(() => {
    if (isSubmitted) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isSubmitted]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.courseDate) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/certificate-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          course_date: formData.courseDate,
          message: formData.message || '',
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Anfrage erfolgreich gesendet!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Ein Fehler ist aufgetreten.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Erfolgsansicht nach Absenden
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <main className="flex-1 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <Card className="border-0 shadow-lg">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Anfrage erfolgreich gesendet!</h2>
                <p className="text-slate-600 mb-6">
                  Vielen Dank für Ihre Anfrage zur Ersatzbescheinigung.<br />
                  Wir prüfen derzeit, ob Ihre Teilnahme in unserem System hinterlegt ist.<br />
                  Sie erhalten in Kürze eine Rückmeldung von uns.
                </p>
                <p className="text-sm text-slate-500 mb-6">
                  Eine Bestätigung wurde an <strong>{formData.email}</strong> gesendet.
                </p>
                <Button 
                  onClick={() => window.location.href = '/'}
                  className="bg-[#1e3a5f] hover:bg-[#152a45]"
                >
                  Zurück zur Startseite
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      <main className="flex-1">
        
        {/* MOBILE: Klassischer Hero wie Dortmund (Bild oben, Titel darunter) */}
        <div className="md:hidden">
          {/* Vollbreites Bild */}
          <div className="w-full">
            <img 
              src="/ersatzbescheinigung-hero.png"
              alt="Ersatzbescheinigung anfordern"
              className="w-full h-auto object-cover"
              style={{ aspectRatio: '16/9' }}
            />
          </div>
          
          {/* Titel-Bereich unter dem Bild - wie Dortmund */}
          <div className="bg-white px-4 py-6">
            <h1 className="text-2xl font-bold text-[#1e3a5f] leading-tight mb-3" data-testid="page-title-mobile">
              Ersatzbescheinigung anfordern
            </h1>
            <p className="text-slate-600 text-base leading-relaxed">
              Bescheinigung verloren? Kein Problem – wir helfen Ihnen schnell und unkompliziert.
            </p>
          </div>
        </div>

        {/* DESKTOP: Bestehender Hero mit Gradient-Overlay (unverändert) */}
        <div className="hidden md:block">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div 
              className="relative overflow-hidden rounded-xl"
              style={{ 
                backgroundImage: `url("/ersatzbescheinigung-hero.png")`,
                backgroundSize: '100% 100%',
                backgroundPosition: 'center center'
              }}
            >
              {/* Gradient Overlay - NUR links für Textbereich */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 via-[#1e3a5f]/70 to-[#1e3a5f]/20" />
              
              {/* Content - Einheitliche Höhe */}
              <div className="relative px-8 lg:px-10 py-12 lg:py-14">
                <div className="flex flex-row items-center justify-between gap-6 min-h-[240px]">
                  {/* Titel + Zusatzinfo - Links */}
                  <div className="max-w-xl">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-full text-sm font-semibold shadow-sm">
                        <FileText className="w-3.5 h-3.5" />
                        Dokument anfordern
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 text-[#1e3a5f] rounded-full text-sm font-semibold shadow-sm">
                        20,00 € Gebühr
                      </span>
                    </div>
                    
                    <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight drop-shadow-sm">
                      Ersatzbescheinigung anfordern
                    </h1>
                    
                    <p className="text-white/90 text-lg mt-3 drop-shadow-sm">
                      Bescheinigung verloren? Kein Problem – wir helfen Ihnen schnell und unkompliziert.
                    </p>
                    
                    {/* Zusätzlicher Teaser-Text */}
                    <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                      <p className="text-white font-semibold text-lg">
                        Haben Sie bei uns einen Kurs besucht?
                      </p>
                      <p className="text-white/80 text-sm mt-1">
                        Füllen Sie das Formular aus – wir prüfen Ihre Teilnahme und stellen eine neue Bescheinigung aus.
                      </p>
                    </div>
                  </div>
                  
                  {/* Info-Box - Rechts, vertikal zentriert */}
                  <div className="flex-shrink-0 flex items-center">
                    <div className="bg-white/95 backdrop-blur-sm rounded-xl p-5 shadow-xl">
                      <div className="flex items-center gap-3 mb-3">
                        <AlertCircle className="w-6 h-6 text-amber-600" />
                        <span className="font-semibold text-[#1e3a5f]">Wichtige Hinweise</span>
                      </div>
                      <ul className="text-sm text-slate-600 space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 mt-1">•</span>
                          <span>Gebühr: 20,00 € (bar bei Abholung)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 mt-1">•</span>
                          <span>Abholung: Sa & So, 09:00–16:30</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
          <section className="py-12 space-y-6">
            {/* 1. Bitte beachten - Text zentriert */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex flex-col items-center text-center text-amber-800">
                <AlertCircle className="w-6 h-6 text-amber-600 mb-3" />
                <p className="font-semibold text-lg mb-3">Bitte beachten Sie:</p>
                <ul className="space-y-2 text-sm">
                  <li>Die Ausstellung ist kostenpflichtig: <strong>20,00 €</strong></li>
                  <li>Zahlung erfolgt <strong>bei Abholung vor Ort</strong> (bar)</li>
                  <li>Abholung: <strong>Samstag & Sonntag, 09:00–16:30 Uhr</strong></li>
                  <li>Adresse: <strong>Poststraße 6, 46236 Bottrop</strong></li>
                </ul>
              </div>
            </div>
            
            {/* 2. So funktioniert's - Text zentriert, Nummern vertikal ausgerichtet */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
              <div className="flex flex-col items-center text-center">
                <h3 className="font-semibold text-[#1e3a5f] mb-3">So funktioniert's:</h3>
                <ol className="space-y-2 text-sm text-slate-600 inline-block text-left">
                  <li className="flex items-center gap-2">
                    <span className="bg-[#1e3a5f] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                    <span>Formular ausfüllen und absenden</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="bg-[#1e3a5f] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                    <span>Wir prüfen Ihre Teilnahme in unserem System</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="bg-[#1e3a5f] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                    <span>Sie erhalten eine Bestätigung per E-Mail</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="bg-[#1e3a5f] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">4</span>
                    <span>Abholung gegen Barzahlung (20 €)</span>
                  </li>
                </ol>
              </div>
            </div>

            {/* 3. Anfrageformular - volle Breite */}
            <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl text-[#1e3a5f] flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Anfrageformular
                  </CardTitle>
                  <p className="text-slate-600 text-sm mt-2">
                    Füllen Sie das Formular aus und wir prüfen, ob Ihre Teilnahme in unserem System hinterlegt ist.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName-desktop" className="flex items-center gap-1">
                          <User className="w-4 h-4 text-slate-400" />
                          Vorname *
                        </Label>
                        <Input 
                          id="firstName-desktop" 
                          name="firstName" 
                          value={formData.firstName} 
                          onChange={handleInputChange} 
                          placeholder="Max"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName-desktop">Nachname *</Label>
                        <Input 
                          id="lastName-desktop" 
                          name="lastName" 
                          value={formData.lastName} 
                          onChange={handleInputChange} 
                          placeholder="Mustermann"
                          required
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email-desktop" className="flex items-center gap-1">
                        <Mail className="w-4 h-4 text-slate-400" />
                        E-Mail-Adresse *
                      </Label>
                      <Input 
                        id="email-desktop" 
                        name="email" 
                        type="email" 
                        value={formData.email} 
                        onChange={handleInputChange} 
                        placeholder="max.mustermann@email.de"
                        required
                      />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone-desktop" className="flex items-center gap-1">
                        <Phone className="w-4 h-4 text-slate-400" />
                        Telefonnummer *
                      </Label>
                      <Input 
                        id="phone-desktop" 
                        name="phone" 
                        type="tel" 
                        value={formData.phone} 
                        onChange={handleInputChange} 
                        placeholder="0123 456789"
                        required
                      />
                    </div>

                    {/* Course Date */}
                    <div className="space-y-2">
                      <Label htmlFor="courseDate-desktop" className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        Ungefähres Kursdatum *
                      </Label>
                      <Input 
                        id="courseDate-desktop" 
                        name="courseDate" 
                        value={formData.courseDate} 
                        onChange={handleInputChange} 
                        placeholder="z.B. Januar 2025 oder 15.01.2025"
                        required
                      />
                      <p className="text-xs text-slate-500">
                        Falls Sie das genaue Datum nicht mehr wissen, geben Sie bitte einen ungefähren Zeitraum an.
                      </p>
                    </div>

                    {/* Message (Optional) */}
                    <div className="space-y-2">
                      <Label htmlFor="message-desktop">Nachricht (optional)</Label>
                      <Textarea 
                        id="message-desktop" 
                        name="message" 
                        value={formData.message} 
                        onChange={handleInputChange} 
                        placeholder="z.B. Kursort oder zusätzliche Hinweise..."
                        rows={3}
                      />
                    </div>

                    {/* Submit Button */}
                    <Button 
                      type="submit" 
                      className="w-full h-12 bg-red-600 hover:bg-red-700 text-base font-semibold"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Wird gesendet...' : 'Ersatzbescheinigung anfordern'}
                    </Button>

                    {/* Hinweis */}
                    <p className="text-xs text-slate-500 text-center">
                      Nach Eingang Ihrer Anfrage prüfen wir, ob Ihre Teilnahme in unserem System hinterlegt ist. 
                      Sie erhalten anschließend eine Rückmeldung.
                    </p>
                  </form>
                </CardContent>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ErsatzbescheinigungPage; 
